import type { ClipboardEvent, FormEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useCreateIssueDraft, useHeroFeatureFlags } from '#src/app/pages/homepage/shared/hooks';
import { isLoaded, isLoading } from '#src/app/shared/utils/async';

import type { AttachedPost, Chip } from './attachedPost';
import { toAttachedPost, toChip } from './attachedPost';
import { getPreviewSuccess, mapPreviewErrorToCode, mapReasonToErrorCode } from './errorCodes';
import type { SocialPostErrorCode } from './errorCodes';
import { submitFreshInput } from './submitFreshInput';
import { useGenerateSocialPostDraft } from './useGenerateSocialPostDraft';
import { useSocialPostPreview } from './useSocialPostPreview';
import { classifySocialPostInput } from './utils';
import type { ClassifyOptions } from './utils';

export type { SocialPostErrorCode } from './errorCodes';

type SocialPostUrlFormHookResult = ModelHookResult<
	{
		isSubmitting: boolean;
		// True while a supported link has been recognized and its preview is still in flight —
		// the UI shows a loading placeholder during this window rather than the chip.
		isResolvingPreview: boolean;
		error: SocialPostErrorCode | undefined;
		chip: Chip | undefined;
		/** Whether the hero accepts any allowlisted link rather than TikTok only; picks the helper copy. */
		multiSource: boolean;
	},
	{
		onSubmit: (event: FormEvent<HTMLFormElement>) => void;
		onPaste: (event: ClipboardEvent<HTMLTextAreaElement>) => void;
		onInputChange: () => void;
		onRemoveChip: () => void;
	}
>;

function useClassifyOptions(): ClassifyOptions {
	const {
		data: { flags },
	} = useHeroFeatureFlags();
	return useMemo(() => ({ multiInputSources: flags.multiInputSources ?? false }), [flags.multiInputSources]);
}

export function useSocialPostUrlForm(): SocialPostUrlFormHookResult {
	const track = useTracking();
	const navigate = useNavigate();
	const classifyOptions = useClassifyOptions();

	const generateDraftMutation = useGenerateSocialPostDraft();
	const createIssueDraftMutation = useCreateIssueDraft({ sourceLocation: 'homepage_tiktok' });

	// A supported link: kicks off its preview immediately, but doesn't show a chip until that
	// preview resolves successfully (see `chip` below).
	const [attachedPost, setAttachedPost] = useState<AttachedPost | undefined>(undefined);
	// Cheap title/image/caption lookup for the attached post. Disabled while no post is attached.
	const previewQuery = useSocialPostPreview(attachedPost?.url);
	// A pre-submit block (unsupported platform / invalid link) surfaced the moment a bad link
	// is pasted; cleared as soon as the user edits the input.
	const [blockError, setBlockError] = useState<SocialPostErrorCode | undefined>(undefined);

	const previewSuccess = getPreviewSuccess(previewQuery.data);
	const previewErrored =
		attachedPost !== undefined && (previewQuery.isError || previewQuery.data?.__typename === 'SocialPostPreviewError');
	const isResolvingPreview = attachedPost !== undefined && previewQuery.isPending;

	const isSubmitting = useMemo(() => {
		if (isLoading(generateDraftMutation) || isLoading(createIssueDraftMutation)) return true;
		if (isLoaded(generateDraftMutation) || isLoaded(createIssueDraftMutation)) return true;
		return false;
	}, [generateDraftMutation, createIssueDraftMutation]);

	const error = useMemo<SocialPostErrorCode | undefined>(() => {
		if (generateDraftMutation.isError) return mapReasonToErrorCode(generateDraftMutation.error.message);
		if (createIssueDraftMutation.isError) return 'generic_error';
		if (previewErrored) return mapPreviewErrorToCode(previewQuery.data);
		return blockError;
	}, [generateDraftMutation, createIssueDraftMutation, previewErrored, previewQuery.data, blockError]);

	const onRemoveChip = useCallback(() => {
		if (attachedPost) {
			// Distinguishes changed-their-mind (dismissed while still neutral, unsubmitted) from
			// gave-up-after-failure (dismissed once the chip had flipped to its error state).
			void track('homepage_social_post_chip_dismissed', { source: attachedPost.url, error: error !== undefined });
		}
		setAttachedPost(undefined);
		generateDraftMutation.reset();
	}, [track, attachedPost, error, generateDraftMutation]);

	const onPaste = useCallback(
		(event: ClipboardEvent<HTMLTextAreaElement>) => {
			const classification = classifySocialPostInput(event.clipboardData.getData('text'), classifyOptions);
			if (classification.kind === 'supported_link') {
				// Let the URL land in the textarea as usual — a resolving indicator renders below
				// it (see isResolvingPreview) until the preview succeeds, at which point the chip
				// replaces the textarea entirely (see `chip` below).
				setBlockError(undefined);
				// A failed freeform submit would otherwise keep its error banner attached to the new chip.
				if (createIssueDraftMutation.isError) createIssueDraftMutation.reset();
				setAttachedPost(toAttachedPost(classification));
				return;
			}
			if (classification.kind === 'unsupported_platform' || classification.kind === 'invalid_link') {
				// Let the text land in the box, but flag it immediately.
				setBlockError(classification.kind);
			}
		},
		[createIssueDraftMutation, classifyOptions],
	);

	const onInputChange = useCallback(() => {
		setBlockError(undefined);
		// A failed preview only ever shows the textarea again once its chip never rendered;
		// clear it so resuming typing starts from a clean slate rather than a stale attachment.
		if (attachedPost && previewErrored) setAttachedPost(undefined);
		// A failed freeform submit's error would otherwise stay visible while the user edits.
		if (createIssueDraftMutation.isError) createIssueDraftMutation.reset();
	}, [attachedPost, previewErrored, createIssueDraftMutation]);

	const onSubmit = useCallback(
		(event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();

			if (attachedPost) {
				void track('homepage_social_post_click', { cta: 'primary', prompt: attachedPost.url });
				// Not yet resolved (still loading, or resolved to an error): nothing to submit yet.
				if (isSubmitting || !previewSuccess) return;
				generateDraftMutation.mutate({ url: attachedPost.url, caption: previewSuccess.caption ?? undefined });
				return;
			}

			const formData = new FormData(event.target as HTMLFormElement);
			const trimmed = ((formData.get('prompt_or_url') as string) || '').trim();
			submitFreshInput(trimmed, {
				classifyOptions,
				track,
				navigate,
				isSubmitting,
				setAttachedPost,
				setBlockError,
				createIssueDraftMutate: createIssueDraftMutation.mutate,
			});
		},
		[
			track,
			isSubmitting,
			attachedPost,
			previewSuccess,
			generateDraftMutation,
			createIssueDraftMutation,
			navigate,
			classifyOptions,
		],
	);

	return {
		data: {
			isSubmitting,
			isResolvingPreview,
			error,
			chip: toChip(attachedPost, previewSuccess),
			multiSource: classifyOptions.multiInputSources,
		},
		actions: {
			onSubmit,
			onPaste,
			onInputChange,
			onRemoveChip,
		},
	};
}
